import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SearchIcon } from '@shopify/polaris-icons';
import { Ic, StatusPill } from '../ui';
import { itApi } from './apiClient';
import { nextSort } from './sorting';
import { Alert, KIND_LABEL, LocChip, STATUS, StatusBadge, WhereCell, WhoCell, dateOnly, dueBadge, money, plural, tagText, useDebounced, useIt } from './common';

function useList(load, deps) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const { version } = useIt();
  useEffect(() => {
    let live = true;
    load().then((d) => { if (live) { setData(d); setError(''); } }, (e) => live && setError(e.message));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, version]);
  return { data, error };
}

function SearchBox({ value, onChange, placeholder }) {
  return <div className="uh-search"><Ic source={SearchIcon} sm /><input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder} /></div>;
}

const Empty = ({ children }) => <tr><td colSpan={12}><div className="uh-empty">{children}</div></td></tr>;

// A column header that sorts the table. `active` is the sorted column (the default one when nothing is chosen).
function SortTh({ k, label, num, active, dir, onSort }) {
  const on = active === k;
  return (
    <th className={num ? 'r' : ''} aria-sort={on ? (dir === 'desc' ? 'descending' : 'ascending') : 'none'}>
      <button type="button" className="uh-ia-sort" onClick={() => onSort(k, !!num)}>{label}<span className={on ? 'on' : ''}>{on ? (dir === 'desc' ? '▼' : '▲') : '↕'}</span></button>
    </th>
  );
}

// Filters kept in the query string (replace, not push), so a filtered list is a link.
// Booleans are '1'; a value equal to its default is left out of the address.
function useUrlFilters(defaults) {
  const [sp, setSp] = useSearchParams();
  const get = (k) => {
    if (!sp.has(k)) return defaults[k];
    const v = sp.get(k);
    if (typeof defaults[k] === 'boolean') return v === '1';
    if (typeof defaults[k] === 'number') return Number(v) || defaults[k];
    return v;
  };
  // Several keys go in one write: the router's functional update starts from the committed
  // address, so two writes in the same tick would overwrite each other.
  const setMany = (values) => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(values)) {
        const val = typeof v === 'boolean' ? (v ? '1' : '') : String(v ?? '');
        if (!val || val === String(defaults[k])) next.delete(k); else next.set(k, val);
      }
      return next;
    }, { replace: true });
  };
  const set = (k) => (v) => setMany({ [k]: v });
  return [get, set, setMany];
}

// ── Assets and Available (one table, two views) ─────────────────────────────

// [key, label, numeric, assets-tab only]
const ACOLS = [['tag', 'Tag'], ['type', 'Type'], ['model', 'Model'], ['hostname', 'Hostname'], ['status', 'Status', 0, 1], ['where', 'Primary Location'], ['holder', 'Who / use', 0, 1], ['price', 'Price', 1, 1]];
// Column widths are fixed (table-layout: fixed) so sorting never reflows them; Model takes what is left.
const ACOL_WIDTH = { tag: 118, type: 92, hostname: 122, status: 108, where: '16%', holder: '15%', price: 96 };

export function AssetsTab({ mode }) {
  const { meta, canManage, openDialog, setScope } = useIt();
  const [get, set, setMany] = useUrlFilters({ q: '', type: '', status: '', kind: '', location: '', review: false, no_receipt: false, sort: '', dir: '', page: 1 });
  const q = get('q'), type = get('type'), status = get('status'), kind = get('kind'), location = get('location'), review = get('review'), noReceipt = get('no_receipt'), sort = get('sort'), dir = get('dir'), page = get('page');
  const setQ = set('q'), setType = set('type'), setStatus = set('status'), setKind = set('kind'), setLocation = set('location'), setReview = set('review'), setNoReceipt = set('no_receipt'), setPage = set('page');
  const defaultSort = mode === 'available' ? 'where' : 'tag';                // what the server orders by when nothing is chosen
  const sortBy = (k, num) => setMany(nextSort({ sort, dir }, k, num, defaultSort));
  const [sel, setSel] = useState({});
  const dq = useDebounced(q);
  const first = React.useRef(true);
  useEffect(() => { if (first.current) { first.current = false; return; } setPage(1); }, [dq, type, status, kind, location, review, noReceipt, sort, dir, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useList(
    () => (mode === 'available' ? itApi.available({ q: dq, type, location_id: location, sort, dir }) : itApi.assets({ q: dq, type, status, kind, location_id: location, review, no_receipt: noReceipt, sort, dir, page })),
    [mode, dq, type, status, kind, location, review, noReceipt, sort, dir, page],
  );
  const filtered = !!(dq.trim() || type || status || kind || location || review || noReceipt);
  useEffect(() => { if (mode === 'assets' && data && data.counts) setScope({ counts: data.counts, filtered }); }, [mode, data, filtered, setScope]);
  useEffect(() => () => setScope(null), [setScope]);
  const rows = data ? data.assets : [];
  const picked = rows.filter((a) => sel[a.id]);
  const allOn = rows.length > 0 && rows.every((a) => sel[a.id]);
  const pageCount = data && data.page_size ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const byType = data && data.by_type ? data.by_type : null;
  return (
    <div className="uh-card">
      <div className="uh-ia-tbar">
        <SearchBox value={q} onChange={setQ} placeholder={mode === 'available' ? 'Tag, model, serial or location' : 'Tag, serial, model, person or location'} />
        <select className="uh-select" aria-label="Type" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All types</option>{meta.types.map((t) => <option key={t}>{t}</option>)}
        </select>
        <select className="uh-select" aria-label="Location" value={location} onChange={(e) => setLocation(e.target.value)}>
          <option value="">All locations</option>{meta.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        {mode === 'assets' && (
          <>
            <select className="uh-select" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>{meta.statuses.map((s) => <option key={s} value={s}>{STATUS[s][0]}</option>)}
            </select>
            <select className="uh-select" aria-label="Location type" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">All location types</option>{meta.location_kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
            </select>
            <label className="uh-ia-check"><input type="checkbox" checked={review} onChange={(e) => setReview(e.target.checked)} /> Needs review</label>
            <label className="uh-ia-check"><input type="checkbox" checked={noReceipt} onChange={(e) => setNoReceipt(e.target.checked)} /> No receipt</label>
          </>
        )}
        <span className="uh-sp" />
        <span className="uh-muted">{data ? (mode === 'available' ? `${rows.length} available` : `${data.total} asset${data.total === 1 ? '' : 's'}`) : ''}</span>
      </div>
      <Alert>{error}</Alert>
      <div className="uh-tscroll">
        <table className="uh-tbl uh-ia-fixed">
          <colgroup>
            <col style={{ width: 34 }} />
            {ACOLS.filter(([, , , assetsOnly]) => mode === 'assets' || !assetsOnly).map(([k]) => <col key={k} style={{ width: mode === 'available' && k === 'where' ? '30%' : ACOL_WIDTH[k] }} />)}
            {mode === 'available' && <col style={{ width: 176 }} />}
          </colgroup>
          <thead><tr>
            <th>{canManage && <input type="checkbox" aria-label="Select all shown" checked={allOn} onChange={(e) => setSel(Object.fromEntries(rows.map((a) => [a.id, e.target.checked])))} />}</th>
            {ACOLS.filter(([, , , assetsOnly]) => mode === 'assets' || !assetsOnly).map(([k, label, num]) => (
              <SortTh key={k} k={k} label={label} num={num} active={sort || defaultSort} dir={sort ? dir : 'asc'} onSort={sortBy} />
            ))}
            {mode === 'available' && <th className="r" />}
          </tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className="click" onClick={() => openDialog({ type: 'asset', id: a.id })}>
                <td onClick={(e) => e.stopPropagation()}>{canManage && <input type="checkbox" aria-label={`Select ${tagText(a)}`} checked={!!sel[a.id]} onChange={(e) => setSel({ ...sel, [a.id]: e.target.checked })} />}</td>
                <td className="nm uh-mono uh-ia-nowrap">{a.tag || <span className="uh-faint">No tag yet</span>}{a.needs_review && <span className="uh-ia-dot" title={a.needs_review} aria-label="Needs review" />}</td>
                <td>{a.type}</td><td>{a.model || <span className="uh-faint">no model</span>}</td>
                <td className="uh-mono uh-ia-nowrap">{a.hostname || <span className="uh-faint">—</span>}</td>
                {mode === 'assets' && <td><StatusBadge status={a.status} /></td>}
                <td><WhereCell a={a} /></td>
                {mode === 'assets' && <td><WhoCell a={a} /></td>}
                <td className="r uh-num">{mode === 'assets' ? (a.price == null ? <span className="uh-faint">no price</span> : money(a.price)) : (canManage && <span className="uh-ia-rowact"><button type="button" className="uh-btn slim" onClick={(e) => { e.stopPropagation(); openDialog({ type: 'move', assets: [a] }); }}>Move</button> <button type="button" className="uh-btn slim primary" onClick={(e) => { e.stopPropagation(); openDialog({ type: 'checkout', assets: [a] }); }}>Check out</button></span>)}</td>
              </tr>
            ))}
            {data && !rows.length && <Empty>{mode === 'available' ? 'Nothing available matches.' : 'No assets match.'}</Empty>}
            {!data && !error && <Empty>Loading…</Empty>}
          </tbody>
        </table>
      </div>
      {mode === 'assets' && pageCount > 1 && (
        <div className="uh-ia-pager"><button type="button" className="uh-btn slim" disabled={page <= 1} onClick={() => setPage(page - 1)}>← Previous</button>
          <span>Page {page} of {pageCount}</span><button type="button" className="uh-btn slim" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>Next →</button></div>
      )}
      {byType && <div className="uh-tfoot"><span>Available by type: {Object.entries(byType).map(([t, n]) => `${t} ${n}`).join(' · ') || 'nothing'}</span></div>}
      {canManage && picked.length > 0 && (
        <div className="uh-ia-bulk" role="region" aria-label="Selected devices">
          <b>{picked.length} selected</b>
          <button type="button" className="uh-btn" onClick={() => openDialog({ type: 'checkout', assets: picked })} disabled={picked.some((a) => !['stock', 'deployed', 'loan'].includes(a.status))}>Check out</button>
          <button type="button" className="uh-btn" disabled={picked.some((a) => a.status !== 'loan' && a.status !== 'deployed')} onClick={() => openDialog({ type: 'checkin', assets: picked })}>Check in / unassign</button>
          <button type="button" className="uh-btn" onClick={() => openDialog({ type: 'move', assets: picked })}>Move</button>
          <button type="button" className="uh-btn" onClick={() => openDialog({ type: 'link', assets: picked })}>Link to receipt…</button>
          <button type="button" className="uh-btn" onClick={() => openDialog({ type: 'delete', assets: picked })}>Delete…</button>
          <button type="button" className="uh-ia-clear" onClick={() => setSel({})}>Clear</button>
        </div>
      )}
    </div>
  );
}

// ── Loans ────────────────────────────────────────────────────────────────────

export function LoansTab() {
  const { meta, canManage, openDialog } = useIt();
  const [q, setQ] = useState('');
  const [due, setDue] = useState('');
  const [kind, setKind] = useState('');
  const [event, setEvent] = useState('');
  const dq = useDebounced(q);
  const { data, error } = useList(() => itApi.loans({ q: dq, due, kind, event }), [dq, due, kind, event]);
  const counts = data ? data.due_counts : { over: 0, soon: 0, later: 0, none: 0 };
  return (
    <div className="uh-card">
      <div className="uh-ia-tbar">
        <SearchBox value={q} onChange={setQ} placeholder="Person, tag, item or purpose" />
        <select className="uh-select" aria-label="Return date" value={due} onChange={(e) => setDue(e.target.value)}>
          <option value="">Any return date</option><option value="over">Overdue ({counts.over})</option><option value="soon">Due in 7 days ({counts.soon})</option>
          <option value="later">Later ({counts.later})</option><option value="none">No return date ({counts.none})</option>
        </select>
        <select className="uh-select" aria-label="Event" value={event} onChange={(e) => setEvent(e.target.value)}>
          <option value="">All events</option><option value="none">No event</option>{meta.events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </select>
        <select className="uh-select" aria-label="Location type" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All location types</option>{meta.location_kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
        </select>
        <span className="uh-sp" />
        <span className="uh-muted">{data ? `${data.assets.length} on loan` : ''}</span>
      </div>
      <Alert>{error}</Alert>
      <div className="uh-tscroll"><table className="uh-tbl">
        <thead><tr><th>Tag</th><th>Item</th><th>Lent to</th><th>Where</th><th>Event</th><th>Return date</th><th>Purpose</th><th /></tr></thead>
        <tbody>
          {(data ? data.assets : []).map((a) => (
            <tr key={a.id}>
              <td className="uh-ia-nowrap"><button type="button" className="uh-link uh-mono" onClick={() => openDialog({ type: 'asset', id: a.id })}>{tagText(a)}</button></td>
              <td>{a.model}</td><td><WhoCell a={a} /></td><td><WhereCell a={a} /></td>
              <td>{a.event_name ? <button type="button" className="uh-link" onClick={() => { const ev = meta.events.find((e) => e.id === a.event_id); if (ev) openDialog({ type: 'event', event: { ...ev, location: a.location, store: a.store } }); }}>{a.event_name}</button> : <span className="uh-faint">none</span>}</td>
              <td>{dueBadge(a, meta.today)}</td><td>{a.purpose}</td>
              <td className="r">{canManage && <span className="uh-ia-row" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="uh-link" onClick={() => openDialog({ type: 'checkout', assets: [a], kind: 'deploy' })}>Make long term</button>
                <button type="button" className="uh-btn slim" onClick={() => openDialog({ type: 'checkin', assets: [a] })}>Check in</button></span>}</td>
            </tr>
          ))}
          {data && !data.assets.length && <Empty>No loans match.</Empty>}
          {!data && !error && <Empty>Loading…</Empty>}
        </tbody>
      </table></div>
      <div className="uh-tfoot"><span>Sorted by return date, soonest first. New loans need a return date.</span></div>
    </div>
  );
}

// ── Events ───────────────────────────────────────────────────────────────────

export function EventsTab() {
  const { canManage, openDialog, meta } = useIt();
  const { data, error } = useList(() => itApi.events(), []);
  const state = (e) => (dateOnly(e.ends_on) < meta.today ? 'ended' : dateOnly(e.starts_on) > meta.today ? 'upcoming' : 'active');
  return (
    <div className="uh-card">
      <div className="uh-card-h"><h2 className="uh-h2 lg">Events</h2><span className="uh-sp" /><span className="uh-muted">Devices lent for an event are tied to it.</span>
        {canManage && <button type="button" className="uh-btn slim primary" onClick={() => openDialog({ type: 'addEvent' })}>Add an event</button>}</div>
      <Alert>{error}</Alert>
      <div className="uh-tscroll"><table className="uh-tbl">
        <thead><tr><th>Event</th><th>Dates</th><th>Where</th><th>Counts toward</th><th>Status</th><th className="r">Devices out</th></tr></thead>
        <tbody>
          {(data ? data.events : []).map((e) => {
            const st = state(e);
            return (
              <tr key={e.id} className="click" onClick={() => openDialog({ type: 'event', event: e })}>
                <td className="nm">{e.name}</td><td>{dateOnly(e.starts_on) === dateOnly(e.ends_on) ? dateOnly(e.starts_on) : `${dateOnly(e.starts_on)} to ${dateOnly(e.ends_on)}`}</td>
                <td><LocChip name={e.location} /><div className="uh-ia-sm">{e.kind === 'onsite' ? 'At the store' : 'Offsite venue'}</div></td>
                <td><LocChip name={e.store} kind="store" /></td>
                <td>{st === 'active' ? <StatusPill tone="par">Happening now</StatusPill> : st === 'upcoming' ? <StatusPill tone="neu">Upcoming</StatusPill>
                  : e.devices_out ? <StatusPill tone="can">Ended, {e.devices_out} not returned</StatusPill> : <StatusPill tone="ful">Ended, all back</StatusPill>}</td>
                <td className="r uh-num">{e.devices_out}</td>
              </tr>
            );
          })}
          {data && !data.events.length && <Empty>No events yet. Add one when devices go out for a tent sale or a tournament.</Empty>}
          {!data && !error && <Empty>Loading…</Empty>}
        </tbody>
      </table></div>
    </div>
  );
}

// ── Receipts ─────────────────────────────────────────────────────────────────

const RCOLS = [['code', 'Receipt'], ['vendor', 'Vendor'], ['date', 'Date'], ['order', 'Order #'], ['card', 'Card'], ['items', 'Items', 1], ['total', 'Total', 1], ['file', 'File']];

export function ReceiptsTab() {
  const { meta, canManage, openDialog } = useIt();
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [card, setCard] = useState('');
  const [sort, setSort] = useState('date');
  const [dir, setDir] = useState('desc');
  const [missing, setMissing] = useState(false);
  const dq = useDebounced(q);
  const { data, error } = useList(() => itApi.receipts({ q: dq, sort, dir, missing, date_from: from, date_to: to, card_id: card }), [dq, sort, dir, missing, from, to, card]);
  const month = (back) => { const d = new Date(); const s = new Date(d.getFullYear(), d.getMonth() - back, 1); const e = new Date(d.getFullYear(), d.getMonth() - back + 1, 0); const f = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`; setFrom(f(s)); setTo(f(e)); };
  const click = (k) => { const n = nextSort({ sort, dir }, k, ['total', 'date', 'items'].includes(k)); setSort(n.sort); setDir(n.dir); };
  return (
    <div className="uh-card">
      <div className="uh-ia-tbar">
        <SearchBox value={q} onChange={setQ} placeholder="Vendor, order #, card, or an item tag" />
        <select className="uh-select" aria-label="File" value={missing ? 'missing' : ''} onChange={(e) => setMissing(e.target.value === 'missing')}><option value="">All receipts</option><option value="missing">Missing a file</option></select>
        <select className="uh-select" aria-label="Payment card" value={card} onChange={(e) => setCard(e.target.value)}><option value="">All cards</option>
          {meta.cards.map((c) => <option key={c.id} value={c.id}>{c.last4 ? `•••• ${c.last4}${c.holder ? ` (${c.holder})` : ''}` : (c.label || 'Unknown card')}</option>)}</select>
        <label className="uh-ia-dt">From <input className="uh-ia-inp" type="date" aria-label="From date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="uh-ia-dt">To <input className="uh-ia-inp" type="date" aria-label="To date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} /></label>
        <button type="button" className="uh-link" onClick={() => month(1)}>Last month</button>
        <button type="button" className="uh-link" onClick={() => month(0)}>This month</button>
        {(from || to) && <button type="button" className="uh-link" onClick={() => { setFrom(''); setTo(''); }}>Clear dates</button>}
        <span className="uh-sp" /><span className="uh-muted">{data ? `${data.matched} of ${data.total} receipts` : ''}</span>
        {canManage && <button type="button" className="uh-btn slim" onClick={() => openDialog({ type: 'export', from, to, card })}>Export for accounting</button>}
        {canManage && <button type="button" className="uh-btn slim primary" onClick={() => openDialog({ type: 'addReceipt' })}>Add a purchase</button>}
      </div>
      <Alert>{error}</Alert>
      <div className="uh-tscroll"><table className="uh-tbl">
        <thead><tr>{RCOLS.map(([k, label, num]) => (
          <SortTh key={k} k={k} label={label} num={num} active={sort} dir={dir} onSort={click} />))}</tr></thead>
        <tbody>
          {(data ? data.receipts : []).map((r) => (
            <tr key={r.id} className="click" onClick={() => openDialog({ type: 'receipt', id: r.id })}>
              <td className="uh-mono nm">{r.code}</td><td>{r.vendor}</td><td>{dateOnly(r.purchased_on)}</td><td className="uh-mono">{r.order_number}</td><td>{r.card}</td>
              <td className="r uh-num">{r.items}</td><td className="r uh-num">{money(r.total)}</td>
              <td>{r.has_file ? <StatusPill tone="ful">Attached{r.pages > 1 ? ` · ${r.pages} pages` : ''}</StatusPill> : <StatusPill tone="hold">Missing</StatusPill>}</td>
            </tr>
          ))}
          {data && !data.receipts.length && <Empty>No receipts match.</Empty>}
          {!data && !error && <Empty>Loading…</Empty>}
        </tbody>
      </table></div>
      {data && <div className="uh-tfoot"><span>{(from || to || card) ? `These ${data.matched} receipts total ${money(data.sum_total)}. ` : ''}One receipt can cover many assets. {data.missing} receipt{data.missing === 1 ? ' is' : 's are'} missing a file.</span></div>}
    </div>
  );
}

// ── People ───────────────────────────────────────────────────────────────────

// ── Locations ────────────────────────────────────────────────────────────────

const LOCATION_KINDS = Object.keys(KIND_LABEL);

// One row's name and type, edited in place. Save sends only what changed.
function LocationRow({ l, canManage, onOpen, onPeople, onRemove }) {
  const navigate = useNavigate();
  const open = (status) => navigate(`/it-assets/assets?location=${l.id}${status ? `&status=${status}` : ''}`);
  const { reloadMeta, refresh, notify } = useIt();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(l.name);
  const [kind, setKind] = useState(l.kind);
  const [error, setError] = useState('');
  const start = () => { setName(l.name); setKind(l.kind); setError(''); setEditing(true); };
  const save = async () => {
    try {
      await itApi.editLocation(l.id, { name: name.trim() !== l.name ? name.trim() : undefined, kind: kind !== l.kind ? kind : undefined });
      setEditing(false); await reloadMeta(); refresh();
      notify(name.trim() !== l.name ? `Renamed ${l.name} to ${name.trim()}` : `${l.name} is now ${KIND_LABEL[kind]}`);
    } catch (e) { setError(e.message); }
  };
  const live = l.deployed + l.on_loan + l.in_stock;
  const parts = [[l.deployed, 'deployed', 'deployed'], [l.on_loan, 'on loan', 'loan'], [l.in_stock, 'in stock', 'stock']].filter(([n]) => n);
  if (editing) {
    return (
      <tr>
        <td className="nm"><input className="uh-ia-inp" value={name} onChange={(e) => setName(e.target.value)} aria-label="Location name" autoFocus onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />{error && <div className="uh-ia-err">{error}</div>}</td>
        <td><select className="uh-select" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Location type">{LOCATION_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}</select></td>
        <td className="r uh-num">{live || <span className="uh-faint">0</span>}</td>
        <td className="r uh-num">{l.people || <span className="uh-faint">0</span>}</td>
        <td className="r uh-num">{l.events || <span className="uh-faint">0</span>}</td>
        <td className="r"><span className="uh-ia-acts"><button type="button" className="uh-btn slim primary" disabled={!name.trim()} onClick={save}>Save</button> <button type="button" className="uh-btn slim" onClick={() => setEditing(false)}>Cancel</button></span></td>
      </tr>
    );
  }
  return (
    <tr>
      <td className="nm"><button type="button" className="uh-link" onClick={onOpen} title={`Every device at ${l.name}`}>{l.name}</button></td>
      <td><span className={`uh-ia-lt ${l.kind}`}>{KIND_LABEL[l.kind] || l.kind}</span></td>
      <td className="r uh-num">{live ? <>
        <button type="button" className="uh-link" onClick={() => open('')} title={`Every asset at ${l.name}`}>{live}</button>
        {(parts.length > 1 || l.deployed !== live) && <span className="uh-faint"> · {parts.map(([n, label, status], i) => <React.Fragment key={status}>{i > 0 && ', '}<button type="button" className="uh-link faint" onClick={() => open(status)} title={`Assets ${label} at ${l.name}`}>{n} {label}</button></React.Fragment>)}</span>}
      </> : <span className="uh-faint">none</span>}</td>
      <td className="r uh-num">{l.people ? <button type="button" className="uh-link" onClick={onPeople} title={`People who work at ${l.name}`}>{l.people}</button> : <span className="uh-faint">0</span>}</td>
      <td className="r uh-num">{l.events || <span className="uh-faint">0</span>}</td>
      {canManage && <td className="r"><span className="uh-ia-acts"><button type="button" className="uh-link" onClick={start}>Rename</button> <button type="button" className="uh-link danger" onClick={onRemove}>Remove</button></span></td>}
    </tr>
  );
}

export function LocationsTab() {
  const { canManage, openDialog } = useIt();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const dq = useDebounced(q);
  const { data, error } = useList(() => itApi.locations({ q: dq, kind }), [dq, kind]);
  const rows = data ? data.locations : [];
  return (
    <div className="uh-card">
      <div className="uh-ia-tbar">
        <SearchBox value={q} onChange={setQ} placeholder="Location name" />
        <select className="uh-select" aria-label="Location type" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All types</option>{LOCATION_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
        </select>
        <span className="uh-sp" /><span className="uh-muted">{data ? plural(rows.length, 'location', 'locations') : ''}</span>
        {canManage && <button type="button" className="uh-btn slim primary" onClick={() => openDialog({ type: 'addLocation' })}>Add a location</button>}
      </div>
      <Alert>{error}</Alert>
      <div className="uh-tscroll"><table className="uh-tbl">
        <thead><tr><th>Name</th><th>Type</th><th className="r">Assets</th><th className="r">People</th><th className="r">Events</th>{canManage && <th />}</tr></thead>
        <tbody>
          {rows.map((l) => <LocationRow key={l.id} l={l} canManage={canManage} onOpen={() => navigate(`/it-assets/assets?location=${l.id}`)} onPeople={() => navigate(`/it-assets/people?location=${l.id}`)} onRemove={() => openDialog({ type: 'removeLocation', location: l })} />)}
          {data && !rows.length && <Empty>{q || kind ? 'No location matches.' : 'No locations yet.'}</Empty>}
          {!data && !error && <Empty>Loading…</Empty>}
        </tbody>
      </table></div>
      <div className="uh-tfoot"><span>Click a name or a count to see the assets there. Removing a location asks where its assets, people and events go; the old name leaves every dropdown but stays on old receipts.</span></div>
    </div>
  );
}

// ── People ───────────────────────────────────────────────────────────────────

// One person's name, email and work location, edited in place.
function PersonRow({ p, canManage, onToggle, onRemove }) {
  const { meta, reloadMeta, refresh, notify } = useIt();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(p.name);
  const [email, setEmail] = useState(p.email);
  const [loc, setLoc] = useState(p.location_id ? String(p.location_id) : '');
  const [error, setError] = useState('');
  const start = () => { setName(p.name); setEmail(p.email); setLoc(p.location_id ? String(p.location_id) : ''); setError(''); setEditing(true); };
  const save = async () => {
    try {
      const body = {};
      if (name.trim() !== p.name) body.name = name.trim();
      if (email.trim().toLowerCase() !== p.email.toLowerCase()) body.email = email.trim();
      if ((loc ? Number(loc) : 0) !== (p.location_id || 0)) body.location_id = loc ? Number(loc) : 0;
      if (Object.keys(body).length) await itApi.editPerson(p.id, body);
      setEditing(false); await reloadMeta(); refresh();
      if (Object.keys(body).length) notify(`Updated ${name.trim()}`);
    } catch (e) { setError(e.message); }
  };
  const status = p.left_on ? (p.devices ? <StatusPill tone="can">Left, {p.devices} to collect</StatusPill> : <StatusPill tone="neu">Left</StatusPill>) : <StatusPill tone="ful">Active</StatusPill>;
  if (editing) {
    return (
      <tr>
        <td className="nm"><input className="uh-ia-inp" value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" autoFocus onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />{error && <div className="uh-ia-err">{error}</div>}</td>
        <td><input className="uh-ia-inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" /></td>
        <td><select className="uh-select" value={loc} onChange={(e) => setLoc(e.target.value)} aria-label="Works at"><option value="">Not set</option>{meta.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></td>
        <td className="r uh-num">{p.devices}</td><td>{status}</td>
        <td className="r"><span className="uh-ia-acts"><button type="button" className="uh-btn slim primary" disabled={!name.trim() || !email.trim()} onClick={save}>Save</button> <button type="button" className="uh-btn slim" onClick={() => setEditing(false)}>Cancel</button></span></td>
      </tr>
    );
  }
  return (
    <tr>
      <td className="nm">{p.name}</td><td>{p.email}</td><td>{p.location ? <LocChip name={p.location} /> : <span className="uh-faint">not set</span>}</td><td className="r uh-num">{p.devices}</td>
      <td>{status}</td>
      {canManage && <td className="r"><span className="uh-ia-acts"><button type="button" className="uh-link" onClick={start}>Edit</button> <button type="button" className="uh-link" onClick={onToggle}>{p.left_on ? 'Mark as back' : 'Mark as left'}</button> <button type="button" className="uh-link danger" onClick={onRemove}>Remove</button></span></td>}
    </tr>
  );
}

export function PeopleTab() {
  const { meta, canManage, openDialog, refresh, reloadMeta, notify } = useIt();
  const [get, set] = useUrlFilters({ q: '', location: '' });   // in the address, so the Locations tab can link here
  const q = get('q'); const location = get('location');
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState('');
  const dq = useDebounced(q);
  const { data, error } = useList(() => itApi.people({ q: dq, location_id: location }), [dq, location]);
  const at = location ? meta.locations.find((l) => String(l.id) === String(location)) : null;
  const sync = async () => {
    setSyncing(true); setSyncError('');
    try {
      const r = await itApi.workspaceSync();
      await reloadMeta();
      notify(`Workspace synced: ${r.users} accounts${r.people_suspended ? `, ${r.people_suspended} device users are suspended` : ''}`);
    } catch (e) { setSyncError(e.message); } finally { setSyncing(false); }
  };
  const toggle = async (p) => { await itApi.editPerson(p.id, { left: !p.left_on }); await reloadMeta(); notify(p.left_on ? `${p.name} is back` : `${p.name} marked as left`); refresh(); };
  return (
    <div className="uh-card">
      <div className="uh-ia-tbar">
        <SearchBox value={q} onChange={set('q')} placeholder="Name or email" />
        <select className="uh-select" aria-label="Works at" value={location} onChange={(e) => set('location')(e.target.value)}>
          <option value="">All locations</option>{meta.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <span className="uh-sp" /><span className="uh-muted">{at ? `${data ? plural(data.people.length, 'person works', 'people work') : 'People who work'} at ${at.name}` : 'Everyone who can hold a device, with or without a Hub login'}</span>
        {meta.is_admin && <button type="button" className="uh-btn slim" disabled={syncing} onClick={sync}
          title={meta.workspace_synced_at ? `Last synced ${new Date(meta.workspace_synced_at).toLocaleString()}` : 'Never synced'}>{syncing ? 'Syncing…' : 'Sync from Workspace'}</button>}
        {canManage && <button type="button" className="uh-btn slim primary" onClick={() => openDialog({ type: 'addPerson' })}>Add a person</button>}
      </div>
      <Alert>{error || syncError}</Alert>
      <div className="uh-tscroll"><table className="uh-tbl">
        <thead><tr><th>Name</th><th>Email</th><th>Works at</th><th className="r">Devices held</th><th>Status</th>{canManage && <th />}</tr></thead>
        <tbody>
          {(data ? data.people : []).map((p) => <PersonRow key={p.id} p={p} canManage={canManage} onToggle={() => toggle(p)} onRemove={() => openDialog({ type: 'removePerson', person: p })} />)}
          {data && !data.people.length && <Empty>{at ? `Nobody is set to work at ${at.name}. Edit a person to put them there.` : 'No people yet. Add someone when you lend them a device, or from here.'}</Empty>}
          {!data && !error && <Empty>Loading…</Empty>}
        </tbody>
      </table></div>
      <div className="uh-tfoot"><span>Edit changes a name, email or where someone works. Marking someone as left shows every device still assigned to them. Remove deletes someone who holds nothing.</span></div>
    </div>
  );
}

// ── Scan in ──────────────────────────────────────────────────────────────────

export function ScanTab() {
  const { meta, refresh } = useIt();
  const [loc, setLoc] = useState(meta.locations.length ? String(meta.locations[0].id) : '');
  const [type, setType] = useState('Tablet / iPad');
  const [tag, setTag] = useState('');
  const [serial, setSerial] = useState('');
  const [hostname, setHostname] = useState('');
  const [model, setModel] = useState('');
  const [notes, setNotes] = useState('');
  const [useFor, setUseFor] = useState('');
  const [status, setStatus] = useState('deployed');
  const [scans, setScans] = useState([]);
  const [error, setError] = useState('');
  const tagRef = React.useRef(null);
  const serialRef = React.useRef(null);
  const location = meta.locations.find((l) => String(l.id) === String(loc));
  const hasHostname = (meta.hostname_types || []).includes(type);              // laptops and desktops
  const add = async () => {
    setError('');
    try {
      const r = await itApi.scan({ tag, serial, hostname: hasHostname ? hostname : '', type, model, notes, status, holder_note: useFor, location_id: Number(loc), store_id: location && location.kind === 'store' ? location.id : null });
      setScans([{ tag: tag.trim().toUpperCase(), serial, type, where: location ? location.name : '', matched: r.matched_old_row }, ...scans]);
      setTag(''); setSerial(''); setHostname(''); refresh();
      if (tagRef.current) tagRef.current.focus();
    } catch (e) { setError(e.message); }
  };
  return (
    <div className="uh-ia-scan">
      <div className="uh-card"><div className="uh-card-h"><h2 className="uh-h2 lg">Add or scan in devices</h2></div><div className="uh-card-b">
        <Alert>{error}</Alert>
        <div className="uh-ia-frow">
          <label className="uh-ia-fld"><span className="lab">Where are these devices?</span>
            <select className="uh-select big" value={loc} onChange={(e) => setLoc(e.target.value)}>{meta.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></label>
          <label className="uh-ia-fld"><span className="lab">Type</span>
            <select className="uh-select big" value={type} onChange={(e) => setType(e.target.value)}>{meta.types.map((t) => <option key={t}>{t}</option>)}</select></label>
          <label className="uh-ia-fld"><span className="lab">Status</span>
            <select className="uh-select big" value={status} onChange={(e) => setStatus(e.target.value)}><option value="deployed">Deployed</option><option value="stock">In stock</option></select></label>
          <label className="uh-ia-fld"><span className="lab">Model <span className="uh-faint">(optional)</span></span>
            <input className="uh-ia-inp big" value={model} onChange={(e) => setModel(e.target.value)} placeholder="iPad 9th gen" /></label>
          <label className="uh-ia-fld"><span className="lab">Used for <span className="uh-faint">(optional)</span></span>
            <input className="uh-ia-inp big" value={useFor} onChange={(e) => setUseFor(e.target.value)} placeholder="Register 2, Pressing portal" /></label>
          <label className="uh-ia-fld"><span className="lab">Note <span className="uh-faint">(optional)</span></span>
            <input className="uh-ia-inp big" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Came with the Acme acquisition" /></label>
        </div>
        <label className="uh-ia-fld"><span className="lab">1. Scan the pre-printed SP tag on the device</span>
          <input ref={tagRef} className="uh-ia-inp big uh-mono" value={tag} onChange={(e) => setTag(e.target.value)} placeholder="SP-0000" autoComplete="off" autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (serialRef.current) serialRef.current.focus(); } }} /></label>
        <label className="uh-ia-fld"><span className="lab">2. Scan or type the serial number <span className="uh-faint">(optional)</span></span>
          <input ref={serialRef} className="uh-ia-inp big uh-mono" value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="Serial number" autoComplete="off"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (hasHostname) { const el = document.getElementById('ia-scan-hostname'); if (el) { el.focus(); return; } } add(); } }} /></label>
        {hasHostname && <label className="uh-ia-fld"><span className="lab">3. Hostname <span className="uh-faint">(SoccerPost + the number, optional)</span></span>
          <input id="ia-scan-hostname" className="uh-ia-inp big uh-mono" value={hostname} onChange={(e) => setHostname(e.target.value)} placeholder="SoccerPost123" autoComplete="off"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} /></label>}
        <div className="uh-ia-row"><button type="button" className="uh-btn primary" disabled={!tag.trim() || !loc} onClick={add}>Add device</button>
          <span className="uh-ia-hint">No receipt is needed, so this also works for devices that came with an acquisition. Press Enter after each scan. A scanner types the code and presses Enter for you. A serial that matches an untagged row from the old sheet gets this tag.</span></div>
      </div></div>
      <div className="uh-card"><div className="uh-card-h"><h2 className="uh-h2 lg">This session</h2><span className="uh-sp" /><span className="uh-muted">{scans.length} saved</span></div>
        <div className="uh-tscroll"><table className="uh-tbl"><thead><tr><th>Tag</th><th>Type</th><th>Serial</th><th>Where</th></tr></thead><tbody>
          {scans.map((s, i) => <tr key={i}><td className="nm uh-mono">{s.tag}{s.matched && <span className="uh-ia-sm"> matched an old row</span>}</td><td>{s.type}</td><td className="uh-mono">{s.serial || <span className="uh-faint">none</span>}</td><td>{s.where}</td></tr>)}
          {!scans.length && <Empty>Nothing scanned yet. Scan the first tag to start. Each device is saved as you scan it.</Empty>}
        </tbody></table></div>
      </div>
    </div>
  );
}
